/**
 * Ticket 114.3 — "Closer" front door: mobile-first bottom sheet to
 * create a prospect demo from an opportunity.
 *
 * 3 steps via <Stepper>:
 *   1. Industry & business — industry pre-filled from account.vertical if
 *      it maps to a known demo_industry.key; businessName pre-filled from
 *      account.name.
 *   2. Theme & audience mode (B2C/B2B).
 *   3. Action choice: "Generate + hand off to Pre-Sales" (AWAITING_REFINEMENT)
 *      or "Send draft now" (READY_TO_SEND).
 *
 * Requires demo.create.basic permission — caller must ensure this before
 * rendering the sheet open.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Stepper, type StepperStep } from '@/components/Stepper';
import { createDemo, buildInitialContentMock, type DemoTheme } from '@/api/demos';
import { useDemoIndustries } from '@/pages/demos/hooks/useDemoIndustries';

export interface OpportunityCreateDemoSheetProps {
  open: boolean;
  onClose: () => void;
  opportunityId: string;
  /** Pre-fill fields from the linked account. */
  accountName?: string;
  /** The account's vertical — may map to a demo_industry.key. */
  accountVertical?: string;
  /** Primary country for the tenant (defaults to "PR"). */
  tenantCountry?: string;
}

type AudienceMode = 'B2C' | 'B2B';

const THEMES: DemoTheme[] = ['sunburst', 'editorial', 'quantum', 'aurora'];

const STEP_KEYS = ['industry', 'theme', 'action'] as const;

export function OpportunityCreateDemoSheet({
  open,
  onClose,
  opportunityId,
  accountName = '',
  accountVertical = '',
  tenantCountry = 'PR',
}: OpportunityCreateDemoSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Step management
  const [stepIndex, setStepIndex] = useState(0);

  // Step 1 state
  const [businessName, setBusinessName] = useState(accountName);
  const [selectedIndustryKey, setSelectedIndustryKey] = useState(accountVertical);
  const [selectedCountry] = useState(tenantCountry);

  // Step 2 state
  const [theme, setTheme] = useState<DemoTheme>('sunburst');
  const [audienceMode, setAudienceMode] = useState<AudienceMode>('B2C');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { industries } = useDemoIndustries(selectedCountry);

  const steps: StepperStep[] = [
    { key: STEP_KEYS[0], label: t('demos.closer.sheet.step.industry.title'), valid: !!selectedIndustryKey && !!businessName.trim() },
    { key: STEP_KEYS[1], label: t('demos.closer.sheet.step.theme.title'), valid: !!theme },
    { key: STEP_KEYS[2], label: t('demos.closer.sheet.step.send.title'), valid: true },
  ];

  const canAdvance = steps[stepIndex]?.valid ?? false;

  function handleNext() {
    if (stepIndex < steps.length - 1 && canAdvance) {
      setStepIndex((i) => i + 1);
    }
  }

  function handleBack() {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
    }
  }

  async function handleSubmit(targetStatus: 'AWAITING_REFINEMENT' | 'READY_TO_SEND') {
    if (!selectedIndustryKey || !businessName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const industry = industries.find(
        (ind) => ind.key === selectedIndustryKey && ind.country === selectedCountry,
      );
      const content = industry ? buildInitialContentMock(industry) : undefined;
      const audience = JSON.stringify({ mode: audienceMode });

      const demo = await createDemo({
        country: selectedCountry,
        industryKey: selectedIndustryKey,
        businessName: businessName.trim(),
        theme,
        audience,
        content,
        opportunityId,
        // Status will be set via a separate patch — create defaults to DRAFT,
        // then we immediately transition to the target status.
      });

      // Transition to the desired status (always non-DRAFT for this flow)
      const { updateDemo } = await import('@/api/demos');
      await updateDemo(demo.id, { status: targetStatus });

      onClose();
      navigate(`/demos/${demo.id}`);
    } catch {
      setError(t('demos.closer.sheet.error'));
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet — bottom on mobile, modal on desktop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('demos.closer.sheet.title')}
        className="fixed inset-x-0 bottom-0 z-50 bg-background rounded-t-2xl shadow-2xl
                   sm:inset-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-1/2 sm:-translate-y-1/2
                   sm:w-[480px] sm:rounded-xl sm:bottom-auto"
      >
        {/* Sheet header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-base font-semibold">{t('demos.closer.sheet.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-5 pt-4 pb-2">
          <Stepper
            steps={steps}
            currentIndex={stepIndex}
            onStepClick={(i) => {
              if (i < stepIndex) setStepIndex(i);
            }}
          />
        </div>

        {/* Step content */}
        <div className="px-5 py-4 min-h-[220px]">
          {stepIndex === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('demos.closer.sheet.step.industry.description')}
              </p>
              {/* Industry combobox */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="cs-industry">
                  {t('demos.studio.step.industry.title')}
                </label>
                <select
                  id="cs-industry"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedIndustryKey}
                  onChange={(e) => setSelectedIndustryKey(e.target.value)}
                >
                  <option value="">—</option>
                  {industries.map((ind) => (
                    <option key={`${ind.country}-${ind.key}`} value={ind.key}>
                      {ind.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Business name */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="cs-biz">
                  {t('demos.list.column.businessName')}
                </label>
                <Input
                  id="cs-biz"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder={t('demos.list.column.businessName')}
                />
              </div>
            </div>
          )}

          {stepIndex === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('demos.closer.sheet.step.theme.description')}
              </p>
              {/* Audience mode */}
              <div className="flex gap-3">
                {(['B2C', 'B2B'] as AudienceMode[]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setAudienceMode(mode)}
                    className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors
                      ${audienceMode === mode ? 'border-[color:var(--color-lop-teal)] text-[color:var(--color-lop-teal)] bg-[color:var(--color-lop-teal)]/10' : 'border-border text-muted-foreground'}`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
              {/* Theme picker */}
              <div className="grid grid-cols-2 gap-2">
                {THEMES.map((th) => (
                  <button
                    key={th}
                    type="button"
                    onClick={() => setTheme(th)}
                    className={`rounded-lg border-2 p-3 text-sm font-medium transition-colors text-left
                      ${theme === th ? 'border-[color:var(--color-lop-teal)]' : 'border-border'}`}
                  >
                    <span className="capitalize">{t(`demos.theme.${th}.label`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {stepIndex === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('demos.closer.sheet.step.send.description')}
              </p>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <Button
                className="w-full"
                disabled={submitting}
                onClick={() => handleSubmit('AWAITING_REFINEMENT')}
              >
                {t('demos.closer.sheet.cta.handoff')}
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                disabled={submitting}
                onClick={() => handleSubmit('READY_TO_SEND')}
              >
                {t('demos.closer.sheet.cta.sendNow')}
              </Button>
            </div>
          )}
        </div>

        {/* Footer navigation (steps 0 and 1) */}
        {stepIndex < 2 && (
          <div className="flex justify-between px-5 pb-5 pt-2 border-t border-border">
            <Button
              variant="secondary"
              onClick={handleBack}
              disabled={stepIndex === 0}
            >
              {t('demos.studio.action.back')}
            </Button>
            <Button onClick={handleNext} disabled={!canAdvance}>
              {t('demos.studio.action.next')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
